/**
 * CUR-424 follow-up — execution-order positions over a compliance-audit
 * template structure tree (section order → step order → sub-step order),
 * shared by the client rule-builder vocabulary and the server's
 * forward-reference guards (one ordering, two consumers).
 *
 * A condition may only reference a routing value produced STRICTLY EARLIER in
 * execution order than the row it conditions — a later (or same-position)
 * value cannot have landed when the condition is evaluated. Positions are the
 * global sub-step slots in document order:
 *
 * - a sub-step's own condition is evaluated at that sub-step's slot;
 * - a step's condition is evaluated on step entry — its first sub-step's slot
 *   (for an empty step, the slot its first sub-step would occupy);
 * - a row appended to a parent evaluates just past the parent's current end.
 */

import { parseComplianceAuditTemplateSubStepProduces } from '../../types/compliance/compliance-audit-template';
import type { ComplianceAuditRoutingField } from './compliance-audit-conditions';

// Minimal structural view of the tree — satisfied by both the client
// structure-store payload and the server's guard structure rows.
export interface ExecutionOrderSubStep {
    id: bigint;
    type: string;
    produces?: unknown;
}

export interface ExecutionOrderStep {
    id: bigint;
    SubSteps: ReadonlyArray<ExecutionOrderSubStep>;
}

export interface ExecutionOrderSection {
    id: bigint;
    Steps: ReadonlyArray<ExecutionOrderStep>;
}

/** A routing field annotated with its producer's execution position. */
export interface OrderedRoutingField extends ComplianceAuditRoutingField {
    /** The producing sub-step's slot in global execution order. */
    position: number;
    /** The producing sub-step's id — resolve titles/labels caller-side. */
    subStepId: bigint;
}

export interface ComplianceAuditExecutionOrder {
    /** Classification routing fields in document order, with positions. */
    fields: OrderedRoutingField[];
    /** Sub-step id → its own slot. */
    subStepPositions: Map<bigint, number>;
    /** Step id → its entry slot (first sub-step's position). */
    stepEntryPositions: Map<bigint, number>;
    /** Step id → the slot a newly appended sub-step would occupy. */
    stepAppendPositions: Map<bigint, number>;
    /** Section id → the entry slot a newly appended step would have. */
    sectionAppendPositions: Map<bigint, number>;
}

export function buildComplianceAuditExecutionOrder(
    sections: ReadonlyArray<ExecutionOrderSection>,
): ComplianceAuditExecutionOrder {
    const fields: OrderedRoutingField[] = [];
    const subStepPositions = new Map<bigint, number>();
    const stepEntryPositions = new Map<bigint, number>();
    const stepAppendPositions = new Map<bigint, number>();
    const sectionAppendPositions = new Map<bigint, number>();

    let slot = 0;
    for (const section of sections) {
        for (const step of section.Steps) {
            stepEntryPositions.set(step.id, slot);
            for (const subStep of step.SubSteps) {
                subStepPositions.set(subStep.id, slot);
                if (subStep.type === 'CLASSIFICATION') {
                    const produces = parseComplianceAuditTemplateSubStepProduces(subStep.produces);
                    const name = produces?.name.trim();
                    if (produces && name) {
                        fields.push({ name, options: produces.options, position: slot, subStepId: subStep.id });
                    }
                }
                slot += 1;
            }
            stepAppendPositions.set(step.id, slot);
        }
        sectionAppendPositions.set(section.id, slot);
    }

    return { fields, subStepPositions, stepEntryPositions, stepAppendPositions, sectionAppendPositions };
}
